import axios from 'axios'
import i18next from 'i18next'
import NotificationService from '../service/NotificationService'

const instance = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    'Content-type': 'application/json',
  },
})

// Public instance for anonymous bootstrap endpoints (e.g. Dictionary/GetDictionaryList, which is
// [AllowAnonymous] on the API). It deliberately has NO auth interceptor: the default `instance` forces
// a logout/redirect when there is no token, so routing a pre-login translations call through it bounced
// not-yet-logged-in users to /logout (white screen / redirect loop before the login handoff).
export const publicInstance = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    'Content-type': 'application/json',
  },
})

instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    const webApiKey = localStorage.getItem('webApiKey')
    const employee = JSON.parse(localStorage.getItem('employee') || '{}')
    const culture = localStorage.getItem('culture')

    if (token && webApiKey && employee) {
      config.headers['ChronosAuth'] = token
      config.headers['WebApiKey'] = webApiKey
      config.headers['userId'] = employee.id
      config.headers['dbId'] = ''
      config.headers['deviceId'] = ''
      config.headers['os'] = 'WEB'
      config.headers['language'] = i18next.language.toUpperCase()
      config.headers['Culture'] = culture
    } else {
      NotificationService.showUnauthorizedRedirectionPopup().then(() => {
        LogoutByUnauthorized()
      })
      // Token yokken isteği API'ye hiç gönderme: header'sız istek backend'de
      // "Missing authentication header" hatası üretiyordu. Cancel olarak reddedilir ki
      // response interceptor'ı ikinci bir genel hata popup'ı göstermesin.
      return Promise.reject(new axios.Cancel('Missing auth token'))
    }

    // Convert Date objects to local datetime strings (not UTC)
    if (config.data) {
      config.data = convertDatesToLocalDatetime(config.data)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Helper function to convert Date/moment objects to local datetime strings
function convertDatesToLocalDatetime(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (obj instanceof Date) {
    // Create a new Date with only year, month, day to avoid timezone issues
    const year = obj.getFullYear()
    const month = String(obj.getMonth() + 1).padStart(2, '0')
    const day = String(obj.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}T00:00:00`
  }

  // Handle moment objects
  if (obj._isAMomentObject) {
    return obj.format('YYYY-MM-DDTHH:mm:ss')
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => convertDatesToLocalDatetime(item))
  }

  if (typeof obj === 'object') {
    const converted: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        converted[key] = convertDatesToLocalDatetime(obj[key])
      }
    }
    return converted
  }

  return obj
}

// Hata değil, kullanıcıya bilgi niteliğindeki backend kodları — info ikonu + Uyarı başlığı ile gösterilir
const INFO_LEVEL_BUSINESS_CODES = new Set<string>([
  'TRS_Already_Home_Office_Reservation',
  'TRS_CannotHomeOfficeMoreThanX',
  'TRS_CannotReserveHomeOfficeMoreThanX',
  'TRS_MonthlyReservationLimitExceeded',
])

// Backend, BusinessException ile birlikte çevrilmiş bir başlık (title) döndürebilir.
// title verilmişse popup başlığı olarak kullanılır; verilmemişse (eski response'lar)
// önceki davranışa (severity bazlı sabit başlık) düşülür.
const showBusinessMessage = (code: string, title?: string) => {
  const translatedMessage = i18next.t(code, { ns: 'dictionary' })
  if (INFO_LEVEL_BUSINESS_CODES.has(code)) {
    NotificationService.showCustomPopup(
      translatedMessage,
      'info',
      title || i18next.t('failed.warning-title'),
      i18next.t('buttons.ok')
    )
  } else {
    NotificationService.showErrorPopup(
      title || i18next.t('failed.error-occured'),
      translatedMessage,
      i18next.t('buttons.ok')
    )
  }
}

instance.interceptors.response.use(
  (response) => {
    // Try different case variations for WebApiKey header
    const webApiKey =
      response.headers.webapikey ||
      response.headers.WebApiKey ||
      response.headers['webapikey'] ||
      response.headers['WebApiKey']

    if (webApiKey) {
      localStorage.setItem('webApiKey', webApiKey)
    }

    // Check for TransactionResponseDto with isTransactionSuccess === false (BusinessException returned as HTTP 200)
    const data = response?.data
    if (data && data.isTransactionSuccess === false && data.message) {
      showBusinessMessage(data.message, data.title || data.Title)
      return Promise.reject({ response, isBusinessError: true })
    }

    return response
  },
  (error) => {
    // Token yokken request interceptor'ında iptal edilen istek: kullanıcıya zaten
    // yetkisiz popup'ı gösterildi, ek hata popup'ı gösterme.
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    // Business-rule failures now arrive as HTTP 400 with a TransactionResponseDto body
    // (isTransactionSuccess === false). Show the localized business message exactly like the previous
    // HTTP-200 path did (see the success interceptor above), instead of the generic error popup.
    const businessData = error?.response?.data
    if (businessData && businessData.isTransactionSuccess === false && businessData.message) {
      showBusinessMessage(businessData.message, businessData.title || businessData.Title)
      return Promise.reject({ ...error, isBusinessError: true })
    }

    const exceptionType = error?.response?.data?.exceptionType || error?.response?.data?.ExceptionType
    if (exceptionType === 'Chronos.Common.BusinessException') {
      // BusinessException için popup göster
      const exceptionCode = error?.response?.data?.exceptionMessage || error?.response?.data?.ExceptionMessage
      const exceptionTitle = error?.response?.data?.exceptionTitle || error?.response?.data?.ExceptionTitle
      showBusinessMessage(exceptionCode, exceptionTitle)
      return Promise.reject(error)
    }

    if (error?.response?.status === 401) {
      NotificationService.showUnauthorizedRedirectionPopup().then(() => {
        LogoutByUnauthorized()
      })
    } else {
      if (error?.response?.config?.url === '/Security/UpdateVisitedStatus') {
        NotificationService.showErrorPopup(
          'Hata',
          error.response.data.exceptionMessage || error.response.data.ExceptionMessage,
          'Tamam'
        )
      } else {
        NotificationService.showErrorPopup(
          i18next.t('overlaypanel-texts.error-header'),
          i18next.t('overlaypanel-texts.error-text'),
          i18next.t('buttons.ok')
        )
      }
    }
    return Promise.reject(error)
  }
)

const LogoutByUnauthorized = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('employee')
  localStorage.removeItem('kvkkDisplay')
  localStorage.removeItem('employeePhoto')
  window.location.href = '/logout'
}

export default instance
